import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import api, { apiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { fmtDate, fmtMoneyCur, TAREA_ESTATUS, SEMAFORO_COLOR, CONTACTO_TIPOS, CAL_COLORS, CAL_LABELS } from "../lib/format";
import { Card } from "../components/shared";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { toast } from "sonner";
import { Plus, FileDown, ChevronLeft, ChevronRight, Trash2, CalendarPlus } from "lucide-react";

const F = "flex items-center";

function TareaModal({ open, onClose, pid, tarea, onSaved }) {
  const [f, setF] = useState({});
  const [adjuntos, setAdjuntos] = useState([]);
  useEffect(() => {
    if (open) {
      setF({ titulo: tarea?.titulo || "", descripcion: tarea?.descripcion || "", responsable: tarea?.responsable || "",
        fecha_compromiso: tarea?.fecha_compromiso || "", prioridad: tarea?.prioridad || "Media" });
      setAdjuntos(tarea?.adjuntos || []);
    }
  }, [open, tarea]);
  const subirAdjunto = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !tarea) return;
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post(`/tareas/${tarea.id}/adjuntos`, fd);
      setAdjuntos([...adjuntos, data]); toast.success("Evidencia adjuntada"); onSaved();
    } catch (err) { toast.error(apiError(err)); }
    e.target.value = "";
  };
  const descargar = async (a) => {
    const r = await api.get(`/tareas/${tarea.id}/adjuntos/${a.id}`, { responseType: "blob" });
    const el = document.createElement("a"); el.href = URL.createObjectURL(r.data); el.download = a.nombre; el.click();
  };
  const save = async (e) => {
    e.preventDefault();
    try {
      if (tarea) await api.patch(`/tareas/${tarea.id}`, f);
      else await api.post("/tareas", { property_id: pid, ...f, fecha_compromiso: f.fecha_compromiso || null });
      toast.success(tarea ? "Tarea actualizada" : "Tarea creada");
      onSaved(); onClose();
    } catch (err) { toast.error(apiError(err)); }
  };
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-md" data-testid="tarea-modal">
        <DialogHeader><DialogTitle className="font-display text-[#002B49]">{tarea ? "Editar tarea" : "Nueva tarea"}</DialogTitle></DialogHeader>
        <form onSubmit={save} className="space-y-3">
          <div className="space-y-1.5"><Label>Título *</Label>
            <Input data-testid="tarea-titulo-input" required value={f.titulo || ""} onChange={(e) => setF({ ...f, titulo: e.target.value })} /></div>
          <div className="space-y-1.5"><Label>Descripción</Label>
            <Textarea rows={2} value={f.descripcion || ""} onChange={(e) => setF({ ...f, descripcion: e.target.value })} /></div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5"><Label>Responsable</Label>
              <Input data-testid="tarea-responsable-input" value={f.responsable || ""} onChange={(e) => setF({ ...f, responsable: e.target.value })} /></div>
            <div className="space-y-1.5"><Label>Fecha compromiso</Label>
              <Input data-testid="tarea-fecha-input" type="date" value={f.fecha_compromiso || ""} onChange={(e) => setF({ ...f, fecha_compromiso: e.target.value })} /></div>
          </div>
          <div className="space-y-1.5"><Label>Prioridad</Label>
            <Select value={f.prioridad || "Media"} onValueChange={(v) => setF({ ...f, prioridad: v })}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>{["Alta", "Media", "Baja"].map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
            </Select></div>
          {tarea && (
            <div className="space-y-1.5 border-t border-slate-100 pt-3">
              <Label>Evidencias / adjuntos ({adjuntos.length})</Label>
              {adjuntos.map((a) => (
                <button key={a.id} type="button" onClick={() => descargar(a)}
                  className="w-full text-left text-xs text-[#00A3B5] hover:underline truncate" data-testid={`adjunto-${a.id}`}>
                  📎 {a.nombre} <span className="text-slate-400">· {a.subido_por}</span>
                </button>))}
              <label className="block">
                <span data-testid="tarea-adjuntar-button" className="inline-flex items-center gap-1.5 text-xs font-semibold text-[#002B49] border border-slate-200 rounded-md px-3 py-1.5 cursor-pointer hover:border-[#00A3B5]">
                  <Plus size={12} /> Adjuntar evidencia
                </span>
                <input data-testid="tarea-adjunto-input" type="file" className="hidden" onChange={subirAdjunto} />
              </label>
            </div>
          )}
          <div className="flex justify-end gap-2"><Button type="button" variant="outline" onClick={onClose}>Cancelar</Button>
            <Button data-testid="tarea-guardar-button" type="submit" className="bg-[#00A3B5] hover:bg-[#008C9C] text-white">Guardar</Button></div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function TareasTab({ pid, canEdit }) {
  const [tareas, setTareas] = useState([]);
  const [modal, setModal] = useState({ open: false, tarea: null });
  const load = () => api.get(`/properties/${pid}/tareas`).then((r) => setTareas(r.data));
  useEffect(() => { load(); }, [pid]); // eslint-disable-line
  const cambiar = async (t, estatus) => {
    try { await api.post(`/tareas/${t.id}/estatus`, { estatus, comentario: "Cambio desde tablero" }); load(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const borrar = async (t) => {
    if (!window.confirm(`¿Eliminar la tarea "${t.titulo}"?`)) return;
    try { await api.delete(`/tareas/${t.id}`); load(); } catch (e) { toast.error(apiError(e)); }
  };
  const cols = ["Pendiente", "En curso", "En espera de tercero", "Cerrada"];
  return (
    <div>
      {canEdit && <Button data-testid="nueva-tarea-button" onClick={() => setModal({ open: true, tarea: null })}
        className="mb-4 h-9 gap-2 bg-[#00A3B5] hover:bg-[#008C9C] text-white"><Plus size={14} /> Nueva tarea</Button>}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
        {cols.map((col) => (
          <div key={col} className="bg-slate-100/70 rounded-lg p-3" data-testid={`kanban-col-${col}`}>
            <p className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-3">{col} ({tareas.filter((t) => t.estatus === col).length})</p>
            <div className="space-y-2">
              {tareas.filter((t) => t.estatus === col).map((t) => (
                <Card key={t.id} className="p-3" testId={`tarea-card-${t.id}`}>
                  <div className={`${F} justify-between gap-2`}>
                    <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ background: SEMAFORO_COLOR[t.semaforo] }} />
                    <p className="text-sm font-semibold text-slate-800 flex-1 cursor-pointer" onClick={() => canEdit && setModal({ open: true, tarea: t })}>{t.titulo}</p>
                    {canEdit && <button onClick={() => borrar(t)} className="text-slate-300 hover:text-red-500"><Trash2 size={12} /></button>}
                  </div>
                  <p className="text-xs text-slate-500 mt-1">{t.responsable || "Sin responsable"} · {t.fecha_compromiso ? fmtDate(t.fecha_compromiso) : "sin fecha"}
                    {t.minuta_id && <span className="ml-1 text-[#7C3AED] font-semibold">· acuerdo</span>}</p>
                  {canEdit && (
                    <Select value={t.estatus} onValueChange={(v) => cambiar(t, v)}>
                      <SelectTrigger className="h-7 text-xs mt-2"><SelectValue /></SelectTrigger>
                      <SelectContent>{TAREA_ESTATUS.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                    </Select>)}
                </Card>
              ))}
            </div>
          </div>
        ))}
      </div>
      <TareaModal open={modal.open} tarea={modal.tarea} pid={pid} onClose={() => setModal({ open: false, tarea: null })} onSaved={load} />
    </div>
  );
}

function MinutasTab({ pid, canEdit }) {
  const [minutas, setMinutas] = useState([]);
  const [det, setDet] = useState(null);
  const [nueva, setNueva] = useState(false);
  const [f, setF] = useState({});
  const [ac, setAc] = useState({});
  const load = () => api.get(`/properties/${pid}/minutas`).then((r) => setMinutas(r.data));
  useEffect(() => { load(); }, [pid]); // eslint-disable-line
  const abrir = (m) => api.get(`/minutas/${m.id}`).then((r) => setDet(r.data));
  const crear = async (e) => {
    e.preventDefault();
    try {
      await api.post(`/properties/${pid}/minutas`, { titulo: f.titulo, fecha: f.fecha,
        participantes: (f.participantes || "").split(",").map((x) => x.trim()).filter(Boolean), notas: f.notas || "" });
      toast.success("Minuta creada"); setNueva(false); setF({}); load();
    } catch (err) { toast.error(apiError(err)); }
  };
  const agregarAcuerdo = async (e) => {
    e.preventDefault();
    try {
      await api.post(`/minutas/${det.id}/acuerdos`, { titulo: ac.titulo, responsable: ac.responsable || "", fecha_compromiso: ac.fecha || null });
      toast.success("Acuerdo agregado (creado como tarea)"); setAc({}); abrir(det);
    } catch (err) { toast.error(apiError(err)); }
  };
  const pdf = async (m) => {
    const r = await api.get(`/minutas/${m.id}/pdf`, { responseType: "blob" });
    const a = document.createElement("a"); a.href = URL.createObjectURL(r.data); a.download = `minuta_${m.id}.pdf`; a.click();
  };
  return (
    <div>
      {canEdit && <Button data-testid="nueva-minuta-button" onClick={() => setNueva(true)} className="mb-4 h-9 gap-2 bg-[#00A3B5] hover:bg-[#008C9C] text-white"><Plus size={14} /> Nueva minuta</Button>}
      <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4">
        {minutas.length === 0 && <p className="text-sm text-slate-400">Sin minutas registradas.</p>}
        {minutas.map((m) => (
          <Card key={m.id} className="p-4 cursor-pointer hover:border-[#00A3B5] transition-colors" testId={`minuta-card-${m.id}`}>
            <div onClick={() => abrir(m)}>
              <p className="font-display font-bold text-[#002B49]">{m.titulo}</p>
              <p className="text-xs text-slate-500 mt-1">{fmtDate(m.fecha)} · {m.participantes?.length || 0} participantes</p>
              <p className="text-xs mt-2"><span className="font-bold text-[#00A3B5]">{m.num_acuerdos}</span> acuerdos · <span className={m.acuerdos_abiertos > 0 ? "text-amber-600 font-bold" : "text-emerald-600 font-bold"}>{m.acuerdos_abiertos} abiertos</span></p>
            </div>
          </Card>
        ))}
      </div>
      <Dialog open={nueva} onOpenChange={(v) => !v && setNueva(false)}>
        <DialogContent className="max-w-md" data-testid="minuta-modal">
          <DialogHeader><DialogTitle className="font-display text-[#002B49]">Nueva minuta</DialogTitle></DialogHeader>
          <form onSubmit={crear} className="space-y-3">
            <div className="space-y-1.5"><Label>Título *</Label><Input data-testid="minuta-titulo-input" required value={f.titulo || ""} onChange={(e) => setF({ ...f, titulo: e.target.value })} /></div>
            <div className="space-y-1.5"><Label>Fecha *</Label><Input data-testid="minuta-fecha-input" type="date" required value={f.fecha || ""} onChange={(e) => setF({ ...f, fecha: e.target.value })} /></div>
            <div className="space-y-1.5"><Label>Participantes (separados por coma)</Label><Input value={f.participantes || ""} onChange={(e) => setF({ ...f, participantes: e.target.value })} /></div>
            <div className="space-y-1.5"><Label>Notas</Label><Textarea rows={3} value={f.notas || ""} onChange={(e) => setF({ ...f, notas: e.target.value })} /></div>
            <div className="flex justify-end gap-2"><Button type="button" variant="outline" onClick={() => setNueva(false)}>Cancelar</Button>
              <Button data-testid="minuta-guardar-button" type="submit" className="bg-[#00A3B5] hover:bg-[#008C9C] text-white">Crear minuta</Button></div>
          </form>
        </DialogContent>
      </Dialog>
      <Dialog open={!!det} onOpenChange={(v) => !v && setDet(null)}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="minuta-detalle">
          {det && <>
            <DialogHeader><DialogTitle className="font-display text-[#002B49]">{det.titulo}</DialogTitle></DialogHeader>
            <p className="text-xs text-slate-500 -mt-1">{fmtDate(det.fecha)} · {det.participantes?.join(", ")}</p>
            {det.notas && <p className="text-sm text-slate-600 whitespace-pre-wrap">{det.notas}</p>}
            <div className={`${F} justify-between mt-1`}>
              <p className="text-xs font-bold uppercase tracking-wider text-slate-500">Acuerdos ({det.acuerdos.length})</p>
              <Button variant="outline" onClick={() => pdf(det)} className="h-8 gap-1.5 text-xs" data-testid="minuta-pdf-button"><FileDown size={13} /> PDF ejecutivo</Button>
            </div>
            <div className="space-y-2">
              {det.acuerdos.map((a) => (
                <div key={a.id} className={`${F} gap-2 border border-slate-200 rounded-md p-2.5 text-sm`}>
                  <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ background: SEMAFORO_COLOR[a.semaforo] }} />
                  <span className="flex-1">{a.titulo}</span>
                  <span className="text-xs text-slate-500">{a.responsable || "—"} · {a.fecha_compromiso ? fmtDate(a.fecha_compromiso) : "sin fecha"} · {a.estatus}</span>
                </div>))}
            </div>
            {canEdit && (
              <form onSubmit={agregarAcuerdo} className="grid grid-cols-1 sm:grid-cols-4 gap-2 border-t border-slate-100 pt-3">
                <Input data-testid="acuerdo-titulo-input" className="sm:col-span-2 h-9" placeholder="Nuevo acuerdo/compromiso *" required value={ac.titulo || ""} onChange={(e) => setAc({ ...ac, titulo: e.target.value })} />
                <Input className="h-9" placeholder="Responsable" value={ac.responsable || ""} onChange={(e) => setAc({ ...ac, responsable: e.target.value })} />
                <div className={`${F} gap-2`}>
                  <Input className="h-9" type="date" value={ac.fecha || ""} onChange={(e) => setAc({ ...ac, fecha: e.target.value })} />
                  <Button data-testid="acuerdo-agregar-button" type="submit" className="h-9 bg-[#00A3B5] text-white px-3"><Plus size={14} /></Button>
                </div>
              </form>)}
          </>}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function DirectorioTab({ canEdit, isAdmin }) {
  const [contactos, setContactos] = useState([]);
  const [modal, setModal] = useState(null); // null | {} | contacto
  const load = () => api.get("/contactos").then((r) => setContactos(r.data));
  useEffect(() => { load(); }, []);
  const save = async (e) => {
    e.preventDefault();
    try {
      const body = { nombre: modal.nombre, empresa: modal.empresa || "", tipo: modal.tipo || "Otro",
        telefono: modal.telefono || "", email: modal.email || "", notas: modal.notas || "" };
      if (modal.id) await api.patch(`/contactos/${modal.id}`, body);
      else await api.post("/contactos", body);
      toast.success("Contacto guardado"); setModal(null); load();
    } catch (err) { toast.error(apiError(err)); }
  };
  const borrar = async (c) => {
    if (!window.confirm(`¿Eliminar a ${c.nombre} del directorio?`)) return;
    try { await api.delete(`/contactos/${c.id}`); load(); } catch (e) { toast.error(apiError(e)); }
  };
  return (
    <div>
      {canEdit && <Button data-testid="nuevo-contacto-button" onClick={() => setModal({})} className="mb-4 h-9 gap-2 bg-[#00A3B5] hover:bg-[#008C9C] text-white"><Plus size={14} /> Nuevo contacto</Button>}
      <Card className="overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[700px]">
            <thead><tr className="bg-slate-50 text-left text-xs uppercase tracking-wider text-slate-500">
              <th className="px-4 py-3">Nombre</th><th className="px-4 py-3">Tipo</th><th className="px-4 py-3">Empresa</th>
              <th className="px-4 py-3">Teléfono</th><th className="px-4 py-3">Email</th><th className="px-4 py-3"></th></tr></thead>
            <tbody>
              {contactos.length === 0 && <tr><td colSpan={6} className="px-4 py-8 text-center text-slate-400">Directorio vacío. Agrega gestores, notarías, bancos, autoridades y operadores.</td></tr>}
              {contactos.map((c) => (
                <tr key={c.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`contacto-row-${c.id}`}>
                  <td className="px-4 py-3 font-semibold text-[#002B49] cursor-pointer" onClick={() => canEdit && setModal(c)}>{c.nombre}</td>
                  <td className="px-4 py-3"><span className="text-xs bg-slate-100 rounded-full px-2 py-0.5">{c.tipo}</span></td>
                  <td className="px-4 py-3 text-slate-600">{c.empresa}</td>
                  <td className="px-4 py-3 text-slate-600">{c.telefono}</td>
                  <td className="px-4 py-3 text-slate-600">{c.email}</td>
                  <td className="px-4 py-3 text-right">{isAdmin && <button onClick={() => borrar(c)} className="text-slate-300 hover:text-red-500"><Trash2 size={14} /></button>}</td>
                </tr>))}
            </tbody>
          </table>
        </div>
      </Card>
      <Dialog open={!!modal} onOpenChange={(v) => !v && setModal(null)}>
        <DialogContent className="max-w-md" data-testid="contacto-modal">
          <DialogHeader><DialogTitle className="font-display text-[#002B49]">{modal?.id ? "Editar contacto" : "Nuevo contacto"}</DialogTitle></DialogHeader>
          {modal && <form onSubmit={save} className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label>Nombre *</Label><Input data-testid="contacto-nombre-input" required value={modal.nombre || ""} onChange={(e) => setModal({ ...modal, nombre: e.target.value })} /></div>
              <div className="space-y-1.5"><Label>Tipo</Label>
                <Select value={modal.tipo || "Otro"} onValueChange={(v) => setModal({ ...modal, tipo: v })}>
                  <SelectTrigger data-testid="contacto-tipo-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{CONTACTO_TIPOS.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                </Select></div>
            </div>
            <div className="space-y-1.5"><Label>Empresa</Label><Input value={modal.empresa || ""} onChange={(e) => setModal({ ...modal, empresa: e.target.value })} /></div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label>Teléfono</Label><Input value={modal.telefono || ""} onChange={(e) => setModal({ ...modal, telefono: e.target.value })} /></div>
              <div className="space-y-1.5"><Label>Email</Label><Input type="email" value={modal.email || ""} onChange={(e) => setModal({ ...modal, email: e.target.value })} /></div>
            </div>
            <div className="space-y-1.5"><Label>Notas</Label><Textarea rows={2} value={modal.notas || ""} onChange={(e) => setModal({ ...modal, notas: e.target.value })} /></div>
            <div className="flex justify-end gap-2"><Button type="button" variant="outline" onClick={() => setModal(null)}>Cancelar</Button>
              <Button data-testid="contacto-guardar-button" type="submit" className="bg-[#00A3B5] hover:bg-[#008C9C] text-white">Guardar</Button></div>
          </form>}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function CalendarioTab({ pid, canEdit }) {
  const hoy = new Date();
  const [mes, setMes] = useState({ y: hoy.getFullYear(), m: hoy.getMonth() });
  const [eventos, setEventos] = useState([]);
  const [cita, setCita] = useState(null);
  const desde = `${mes.y}-${String(mes.m + 1).padStart(2, "0")}-01`;
  const finMes = new Date(mes.y, mes.m + 1, 0).getDate();
  const hasta = `${mes.y}-${String(mes.m + 1).padStart(2, "0")}-${finMes}`;
  const load = () => api.get(`/properties/${pid}/calendario`, { params: { desde, hasta } }).then((r) => setEventos(r.data));
  useEffect(() => { load(); }, [pid, mes]); // eslint-disable-line
  const primerDia = new Date(mes.y, mes.m, 1).getDay();
  const celdas = [...Array(primerDia).fill(null), ...Array.from({ length: finMes }, (_, i) => i + 1)];
  const porDia = {};
  eventos.forEach((e) => { const d = parseInt(e.fecha.slice(8, 10), 10); (porDia[d] = porDia[d] || []).push(e); });
  const nombreMes = new Date(mes.y, mes.m, 1).toLocaleDateString("es-MX", { month: "long", year: "numeric" });
  const crearCita = async (e) => {
    e.preventDefault();
    try {
      await api.post(`/properties/${pid}/eventos`, { titulo: cita.titulo, fecha: cita.fecha, descripcion: cita.descripcion || "" });
      toast.success("Cita agregada al calendario"); setCita(null); load();
    } catch (err) { toast.error(apiError(err)); }
  };
  return (
    <div>
      <div className={`${F} justify-between mb-4 flex-wrap gap-3`}>
        <div className={`${F} gap-2`}>
          <Button variant="outline" className="h-8 px-2" onClick={() => setMes(mes.m === 0 ? { y: mes.y - 1, m: 11 } : { y: mes.y, m: mes.m - 1 })}><ChevronLeft size={15} /></Button>
          <p className="font-display font-bold text-[#002B49] capitalize w-44 text-center" data-testid="calendario-mes">{nombreMes}</p>
          <Button variant="outline" className="h-8 px-2" onClick={() => setMes(mes.m === 11 ? { y: mes.y + 1, m: 0 } : { y: mes.y, m: mes.m + 1 })}><ChevronRight size={15} /></Button>
        </div>
        {canEdit && <Button data-testid="nueva-cita-button" onClick={() => setCita({ fecha: desde })} className="h-9 gap-2 bg-[#00A3B5] hover:bg-[#008C9C] text-white"><CalendarPlus size={14} /> Agregar cita</Button>}
      </div>
      <Card className="p-4 overflow-x-auto">
        <div className="grid grid-cols-7 gap-1 min-w-[720px]">
          {["Dom", "Lun", "Mar", "Mié", "Jue", "Vie", "Sáb"].map((d) => <p key={d} className="text-[10px] uppercase tracking-wider text-slate-400 font-bold text-center py-1">{d}</p>)}
          {celdas.map((d, i) => (
            <div key={i} className={`min-h-[86px] rounded-md border p-1.5 ${d ? "border-slate-100 bg-white" : "border-transparent"}`}>
              {d && <>
                <p className="text-xs font-bold text-slate-400">{d}</p>
                <div className="space-y-0.5 mt-1">
                  {(porDia[d] || []).slice(0, 3).map((e, j) => (
                    <p key={j} title={`${CAL_LABELS[e.tipo] || e.tipo}: ${e.titulo}`} className="text-[10px] truncate rounded px-1 py-0.5 text-white font-semibold" style={{ background: CAL_COLORS[e.tipo] || "#64748B" }}>{e.titulo}</p>
                  ))}
                  {(porDia[d] || []).length > 3 && <p className="text-[9px] text-slate-400">+{porDia[d].length - 3} más</p>}
                </div>
              </>}
            </div>))}
        </div>
        <div className="flex flex-wrap gap-3 mt-4 pt-3 border-t border-slate-100">
          {Object.entries(CAL_LABELS).map(([k, l]) => (
            <span key={k} className={`${F} gap-1.5 text-xs text-slate-500`}><span className="w-2.5 h-2.5 rounded-sm" style={{ background: CAL_COLORS[k] }} />{l}</span>))}
        </div>
      </Card>
      <Dialog open={!!cita} onOpenChange={(v) => !v && setCita(null)}>
        <DialogContent className="max-w-sm" data-testid="cita-modal">
          <DialogHeader><DialogTitle className="font-display text-[#002B49]">Agregar cita / evento</DialogTitle></DialogHeader>
          {cita && <form onSubmit={crearCita} className="space-y-3">
            <div className="space-y-1.5"><Label>Título *</Label><Input data-testid="cita-titulo-input" required value={cita.titulo || ""} onChange={(e) => setCita({ ...cita, titulo: e.target.value })} placeholder="Cita en ventanilla / autoridad" /></div>
            <div className="space-y-1.5"><Label>Fecha *</Label><Input data-testid="cita-fecha-input" type="date" required value={cita.fecha || ""} onChange={(e) => setCita({ ...cita, fecha: e.target.value })} /></div>
            <div className="space-y-1.5"><Label>Descripción</Label><Textarea rows={2} value={cita.descripcion || ""} onChange={(e) => setCita({ ...cita, descripcion: e.target.value })} /></div>
            <div className="flex justify-end gap-2"><Button type="button" variant="outline" onClick={() => setCita(null)}>Cancelar</Button>
              <Button data-testid="cita-guardar-button" type="submit" className="bg-[#00A3B5] hover:bg-[#008C9C] text-white">Guardar</Button></div>
          </form>}
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default function Gestion() {
  const { pid } = useParams();
  const { canEdit, isAdmin } = useAuth();
  const [tab, setTab] = useState("tareas");
  const tabs = [["tareas", "Tareas y pendientes"], ["minutas", "Minutas y acuerdos"], ["directorio", "Directorio"], ["calendario", "Calendario"]];
  return (
    <div className="p-4 sm:p-8">
      <p className="text-xs tracking-[0.25em] uppercase text-[#00A3B5] font-bold mb-1">Módulo Gestión</p>
      <h1 className="font-display text-3xl font-bold text-[#002B49] mb-5">Coordinación del proyecto</h1>
      <div className="flex gap-1 border-b border-slate-200 mb-6 overflow-x-auto">
        {tabs.map(([k, l]) => (
          <button key={k} data-testid={`gestion-tab-${k}`} onClick={() => setTab(k)}
            className={`px-4 py-2.5 text-sm font-semibold border-b-2 whitespace-nowrap transition-colors ${tab === k ? "border-[#00A3B5] text-[#002B49]" : "border-transparent text-slate-400 hover:text-slate-600"}`}>{l}</button>))}
      </div>
      {tab === "tareas" && <TareasTab pid={pid} canEdit={canEdit} />}
      {tab === "minutas" && <MinutasTab pid={pid} canEdit={canEdit} />}
      {tab === "directorio" && <DirectorioTab canEdit={canEdit} isAdmin={isAdmin} />}
      {tab === "calendario" && <CalendarioTab pid={pid} canEdit={canEdit} />}
    </div>
  );
}
